import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import ResearchDisclaimer from '../../../Components/Common/ResearchDisclaimer';

/**
 * Footer.
 *
 * The social row was removed rather than repointed: all five icons linked to
 * "#", and one of them was Dribbble, which came with the template. An icon that
 * goes nowhere is worse than no icon — it reads as an account that exists.
 * They belong back here when the accounts do.
 *
 * Two links were dead: /documentation has no route, and /reports is /report.
 * Every href below was checked against Routes/allRoutes.js.
 *
 * In-page jumps have to name the landing page when that is not where the
 * reader is. A bare "#faqs" resolves against the current route, so from
 * /contact it produced /contact#faqs -- a fragment matching nothing, on a page
 * with no such section. This footer appears on the policy pages, the sign-in
 * screens, the error pages and every public forecast, so that was most of the
 * places it renders.
 */
const Footer = () => {
    const { pathname } = useLocation();

    // On the landing page a bare fragment is right: "/#faqs" there is a
    // full-page load of the page already open.
    const section = (id) => (pathname === '/' ? `#${id}` : `/#${id}`);

    return (
        <React.Fragment>
            <footer className="custom-footer bg-dark py-5 position-relative">
                <Container>
                    <Row>
                        <Col lg={4} className="mt-4 fs-13">
                            <div>
                                <h4 className="text-white fw-bold">ForecastNest</h4>
                                <div className="mt-4">
                                    <p className="text-white-50">
                                        Research management for people who use AI
                                    </p>
                                    <p className="ff-secondary text-white-50">
                                        A standard way to ask your own AI about a market, a permanent
                                        record of what it said, and an honest answer about whether it
                                        held up. Bring whichever model you already pay for.
                                    </p>
                                </div>
                            </div>
                        </Col>

                        <Col lg={7} className="ms-lg-auto">
                            <Row>
                                <Col sm={4} className="mt-4">
                                    <h5 className="text-white mb-0">Explore</h5>
                                    <div className="text-muted mt-3">
                                        <ul className="list-unstyled ff-secondary footer-list">
                                            <li><a href={section("services")}>What it does</a></li>
                                            <li><a href={section("how-it-works")}>How it works</a></li>
                                            <li><a href={section("your-template")}>Your own template</a></li>
                                            <li><a href={section("faqs")}>FAQ</a></li>
                                            {/* A plain anchor, not a Link: the blog is a
                                                separate subdomain, and react-router would
                                                try to match it as an in-app route and land
                                                on the catch-all instead. */}
                                            <li><a href="https://insights.forecastnest.com/">Insights &mdash; the blog</a></li>
                                            <li><Link to="/contact">Contact</Link></li>
                                        </ul>
                                    </div>
                                </Col>
                                <Col sm={4} className="mt-4">
                                    <h5 className="text-white mb-0">Your research</h5>
                                    <div className="text-muted mt-3">
                                        <ul className="list-unstyled ff-secondary footer-list">
                                            <li><Link to="/register">Create an account</Link></li>
                                            <li><Link to="/login">Sign in</Link></li>
                                            <li><Link to="/my-research/generate">Generate research</Link></li>
                                            <li><Link to="/my-research">My research</Link></li>
                                            <li><Link to="/how-scoring-works">How scoring works</Link></li>
                                        </ul>
                                    </div>
                                </Col>
                                <Col sm={4} className="mt-4">
                                    <h5 className="text-white mb-0">Legal</h5>
                                    <div className="text-muted mt-3">
                                        <ul className="list-unstyled ff-secondary footer-list">
                                            <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                                            <li><Link to="/terms-condition">Terms of Service</Link></li>
                                            <li><Link to="/pricing">Pricing</Link></li>
                                            <li><Link to="/refund-policy">Cancellation &amp; Refunds</Link></li>
                                            <li><Link to="/contact">Contact Us</Link></li>
                                        </ul>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    <Row className="mt-5">
                        <Col lg={12}>
                            <ResearchDisclaimer textClass="text-white-50" />
                        </Col>
                    </Row>

                    <Row className="text-center text-sm-start align-items-center mt-4">
                        <Col sm={12}>
                            <p className="copy-rights mb-0">
                                © {new Date().getFullYear()} ForecastNest
                            </p>
                        </Col>
                    </Row>
                </Container>
            </footer>
        </React.Fragment>
    );
};

export default Footer;
